import cx from 'classnames'
import { extractProfileInfo } from 'components/ExternalIntegrationSelect/utils'
import Styles from './TikTokPreview.module.scss'
import { TikTokIntegration } from 'api/clients/ExternalIntegrationsClient'
import Silhouette from 'components/Previews/InstagramPreview/silhouette.jpg'
import { LikeIcon, CommentIcon, BookmarkIcon, ShareIcon } from './icons'
import { Search, Plus02 } from 'blaze-ui/icons'
import { highlightContent } from '../highlightContent'
import { MediaObject } from 'types'
import { isVideo } from 'components/ImagePickerModal/utils'
import { ReactNode } from 'react'
import { Button } from 'blaze-ui/Button'
import { Edit1 } from 'blaze-ui/icons'
import { ReelsVideoPlayer } from '../components/ReelsVideoPlayer/ReelsVideoPlayer'
import { useVideoProgress } from '../hooks/useVideoProgress'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { EditInDesignerOverlay } from '../components/EditInDesignerOverlay/EditInDesignerOverlay'
import { CaptionSkeleton } from '../components/CaptionSkeleton/CaptionSkeleton'
import { DesignChannel } from 'components/NewFileModal/Types'

interface TikTokPreviewProps {
  integration?: TikTokIntegration
  mediaObjects?: MediaObject[]
  content?: string
  loading?: boolean
  onEdit?: () => void
  containerClassName?: string
  customPreviewContent?: ReactNode
  showVideoControls?: boolean
  isActive?: boolean
  isTransitioning?: boolean
  showHoverActionOverlay?: boolean
  almanacFileKey?: string
  isGenerating?: boolean
  onClose?: () => void
}

export const TikTokPreview = ({
  integration,
  mediaObjects,
  content,
  loading,
  onEdit,
  containerClassName,
  customPreviewContent,
  showVideoControls,
  isActive = true,
  isTransitioning = false,
  isGenerating = false,
  showHoverActionOverlay = false,
  almanacFileKey,
  onClose,
}: TikTokPreviewProps) => {
  const { name, username, avatar } = integration
    ? extractProfileInfo(integration)
    : { name: 'User', username: 'user', avatar: Silhouette }

  const highlightClassNames = {
    mentionClassName: Styles.highlight,
    hashtagClassName: Styles.highlight,
  }

  const video = mediaObjects?.length && isVideo({ url: mediaObjects[0].source_url, type: mediaObjects[0].mime_type })
  const videoUrl = video ? mediaObjects[0].source_url : undefined
  const videoDuration = mediaObjects?.[0]?.duration

  const { videoRef, progress } = useVideoProgress({ videoUrl, duration: videoDuration })

  return (
    <ConditionalWrapper
      condition={showHoverActionOverlay && almanacFileKey}
      wrapper={(children) => (
        <EditInDesignerOverlay
          channel={DesignChannel.TikTokPost}
          almanacFileKey={almanacFileKey as string}
          borderRadius={16}
          position={video ? 'top' : undefined}
          onClose={onClose}
          className={isGenerating ? Styles.disableHover : undefined}
          caption={content}
        >
          {children}
        </EditInDesignerOverlay>
      )}
    >
      <div className={cx(Styles.container, containerClassName)}>
        <div className={Styles.content}>
          {customPreviewContent || (
            <ReelsVideoPlayer
              mediaObjects={mediaObjects}
              showVideoControls={showVideoControls}
              isActive={isActive}
              className={Styles.preview}
              dataTestId="tiktok-video"
              username={username}
              videoRef={videoRef}
              almanacFileKey={almanacFileKey as string}
              isGenerating={isGenerating}
            />
          )}
        </div>
        <div className={Styles.footer}>
          <div className={Styles.nameAndCaption}>
            <div className={Styles.username}>{`@${name}`}</div>
            {onEdit && (
              <Button variant="tertiary" onPress={onEdit} frontIcon={Edit1}>
                Edit
              </Button>
            )}
            <div className={Styles.caption}>
              {isGenerating ? (
                <CaptionSkeleton />
              ) : (
                <span
                  key={`caption-${content || ''}`}
                  dangerouslySetInnerHTML={{
                    __html: highlightContent(content || '', highlightClassNames),
                  }}
                />
              )}
            </div>
          </div>
        </div>
        <div className={Styles.topActions}>
          <div className={Styles.categories}>
            <div>Following</div>
            <div>Shop</div>
            <div className={Styles.activeTab}>For You</div>
          </div>
          <div className={Styles.searchIcon}>
            <Search />
          </div>
        </div>
        <div className={Styles.actions}>
          <div className={Styles.avatarContainer}>
            <img src={avatar || Silhouette} alt={username} className={Styles.avatar} />
            <div className={Styles.plusIcon}>
              <Plus02 color="white" size={14} />
            </div>
          </div>
          <div className={Styles.action}>
            <LikeIcon />
            <span>223</span>
          </div>
          <div className={Styles.action}>
            <CommentIcon />
            <span>23</span>
          </div>
          <div className={Styles.action}>
            <BookmarkIcon />
            <span>10</span>
          </div>
          <div className={Styles.action}>
            <ShareIcon />
            <span>3</span>
          </div>
          <img src={avatar || Silhouette} alt={username} className={Styles.profile} />
        </div>
        <div className={Styles.loadingBar}>
          <div className={Styles.progress} style={{ width: `${progress}%` }} />
        </div>
      </div>
    </ConditionalWrapper>
  )
}
